import * as React from 'react';

import ListIcon from '@mui/icons-material/List';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { type User } from '@/auth';
import Log from '@/Logger';
import { useConfirmDialog } from '../../util/useConfirmDialog';

import { enabledTenantFeatureSetting, getSettings } from './api';

type Props = {
    user: User;
};

type TenantFeatureSetting = {
    icon: React.JSX.Element;
    key: string;
    label: string;
    state: boolean;

    enablingMessage?: string;
    enabledMessage?: string;
    disablingMessage?: string;
    disabledMessage?: string;
}

function icon(f: { id: string }) {
    if (f.id === 'ddl') return (<ListIcon />);

    return (<SettingsOutlinedIcon />);
}

export default function DmsSettings(props: Props) {
    const logger = Log.getLogger();

    const [features, setFeatures] = React.useState<TenantFeatureSetting[]>([]);
    const [pending, setPending] = React.useState(true);
    const [ConfirmDialog, { openConfirmDialog, setConfirmDialogMessage }] = useConfirmDialog();

    // デバイスを取得
    React.useEffect(() => {
        getSettings()
            .then(features => {
                if (features) {
                    const s = features.map(f => ({
                        icon: icon(f),
                        key: f.id,
                        label: f.name,
                        state: f.state === 'ENABLED',
                        enablingMessage: f.enablingMessage,
                        enabledMessage: f.enabledMessage,
                        disablingMessage: f.disablingMessage,
                        disabledMessage: f.disabledMessage,
                    }));
                    setFeatures(s);
                }

                setPending(false);
            })
            .catch(() => {
                setPending(false);
            })
    }, []);

    async function handleToggle_enabled(key: string) {
        logger.ui.info('Settings', 'handleToggle', `${key} enabled`);

        const msg = features.find(f => f.key === key);
        if (msg && msg.enablingMessage) {
            setConfirmDialogMessage([msg.enablingMessage], true);

            const buttonEvent = await openConfirmDialog();
            if (buttonEvent !== 'confirm') {
                return;
            }
        }

        const x = await enabledTenantFeatureSetting(key, 'ENABLED')

        const newFeatures = [...features];
        const idx = newFeatures.findIndex(f => f.key === x.id)
        if (idx !== -1) {
            newFeatures[idx].state = x.state === 'ENABLED'
        }

        setFeatures(newFeatures);
    }

    async function handleToggle_disabled(key: string) {
        logger.ui.info('Settings', 'handleToggle', `${key} disabled`);

        const msg = features.find(f => f.key === key);
        if (msg && msg.disablingMessage) {
            setConfirmDialogMessage([msg.disablingMessage], true);

            const buttonEvent = await openConfirmDialog();
            if (buttonEvent !== 'confirm') {
                return;
            }
        }

        const x = await enabledTenantFeatureSetting(key, 'DISABLED')

        const newFeatures = [...features];
        const idx = newFeatures.findIndex(f => f.key === x.id)
        if (idx !== -1) {
            newFeatures[idx].state = x.state === 'ENABLED'
        }

        setFeatures(newFeatures);
    }

    const handleToggle = (key: string, newState: boolean) => async () => {
        if (newState) {
            await handleToggle_enabled(key);
        } else {
            await handleToggle_disabled(key);
        }
    };

    return (
        <>

            <List
                sx={{ width: '100%', bgcolor: 'background.paper' }}
                subheader={<ListSubheader
                    sx={{ fontSize: '1.5rem', fontWeight: 'bold', mt: 2 }} // フォントサイズを調整
                >
                    ドキュメント管理機能設定
                </ListSubheader>
                }
            >
                <Box component="section" marginLeft={3} marginBottom={1}>
                    <Typography color="text.secondary" variant="body2">
                        お客様のテナント全体の設定ができます
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        お客様の環境の全ユーザー、全デバイスの共通設定を変更します。
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        デバイスの設定を変更した場合はデバイスを<strong style={{ color: 'red' }}>2回再起動</strong>してください
                    </Typography>
                </Box>
                <Divider />
                {
                    pending ? <LinearProgress /> :
                        features == null || features.length === 0 ? <Typography margin={1}>設定項目なし</Typography> :
                            features.map((item) => (
                                <ListItem
                                    key={item.key}
                                    divider={true}
                                >
                                    <ListItemIcon sx={{ color: 'inherit', marginLeft: '25px' }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText id={'switch-list-label-' + item.key} primary={item.label} />
                                    <Switch
                                        edge="start"
                                        onChange={handleToggle(item.key, !item.state)}
                                        checked={item.state}
                                    />
                                </ListItem>
                            ))
                }
                <ConfirmDialog key={'settings'} title='設定を変更しますか？' />
            </List>
        </>
    );
};